import React, { useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  AppBar,
  Toolbar,
  Button
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

const AccountSupport: React.FC = () => {
  const navigate = useNavigate();
  const { product } = useParams<{ product: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const isTracsess = product === 'tracsess';
  const appName = isTracsess ? 'Tracsess' : 'Nexpenx';
  const supportEmail = isTracsess ? 'support@tracsess.com' : 'contact@prodxpress.com';
  const appColor = isTracsess ? '#1357A6' : '#1a4d1a';
  const websiteUrl = isTracsess ? 'https://tracsess.com' : null;

  return (
    <Box>
      {/* Header */}
      <AppBar position="fixed" elevation={0} sx={{ backgroundColor: 'white', color: 'text.primary' }}>
        <Toolbar>
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              flexGrow: 1, 
              cursor: 'pointer',
              '&:hover': { opacity: 0.8 }
            }}
            onClick={() => navigate('/')}
          >
            <img 
              src="/logo-square.png" 
              alt="Prodxpress Logo" 
              style={{ 
                height: '40px', 
                marginRight: '12px',
                maxWidth: '50px'
              }}
            />
            <img 
              src="/logo-title.png" 
              alt="PRODXPRESS" 
              style={{ 
                height: '24px', 
                maxWidth: '150px',
                objectFit: 'contain'
              }}
            />
          </Box>
          <Button 
            variant="outlined" 
            onClick={() => navigate(`/products/${product}`)}
            sx={{ ml: 2 }}
          >
            Back to {appName}
          </Button>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box sx={{ pt: { xs: 10, md: 12 }, pb: { xs: 6, md: 8 }, backgroundColor: '#F8FAFC', minHeight: '100vh' }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              {appName} Account Deletion
            </Typography>
            <Typography variant="h2" sx={{ 
              color: 'text.primary', 
              fontWeight: 700, 
              mb: 3,
              fontSize: { xs: '2rem', md: '3rem' }
            }}>
              {appName}
            </Typography>
          </Box>

          <Box sx={{ backgroundColor: 'white', borderRadius: 3, p: { xs: 3, md: 5 }, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            <Typography variant="h4" gutterBottom sx={{ 
              color: 'text.primary', 
              fontWeight: 600, 
              mb: 4,
              fontSize: { xs: '1.5rem', md: '2rem' }
            }}>
              Account Data Deletion Instructions
            </Typography>

            {/* For Installed Users */}
            <Box sx={{ mb: 5 }}>
              <Typography variant="h5" gutterBottom sx={{ 
                color: appColor, 
                fontWeight: 600, 
                mb: 3,
                fontSize: { xs: '1.25rem', md: '1.5rem' }
              }}>
                For Installed Users:
              </Typography>
              
              <Typography variant="h6" gutterBottom sx={{ 
                color: 'text.primary', 
                fontWeight: 600, 
                mb: 2 
              }}>
                Deleting Your Account Data in {appName}
              </Typography>
              
              <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.6 }}>
                To delete your account data in {appName}, follow these simple steps:
              </Typography>

              <Box component="ul" sx={{ 
                color: 'text.secondary', 
                pl: 3, 
                mb: 4,
                '& li': { 
                  mb: 1.5, 
                  fontSize: { xs: '0.95rem', md: '1rem' },
                  lineHeight: 1.6 
                }
              }}>
                <li>Open the {appName} app on your device.</li>
                <li>Navigate to Profile page.</li>
                {isTracsess && <li>Click Edit button.</li>}
                <li>Locate and select "Delete Account" option {isTracsess ? 'at the bottom of the page' : 'at the bottom of the page'}.</li>
                <li>Follow the on-screen prompts to confirm and complete the deletion process.</li>
              </Box>

              <Typography variant="body1" sx={{ 
                color: 'text.secondary', 
                mb: 2, 
                lineHeight: 1.6,
                fontStyle: 'italic' 
              }}>
                Our support team will process your request, and you'll receive a confirmation email once your account data has been deleted. Please note that this process may take a few days.
              </Typography>

              <Typography variant="body1" sx={{ 
                color: 'text.secondary', 
                lineHeight: 1.6,
                fontWeight: 500 
              }}>
                Your account data will be securely deleted from our servers.
              </Typography>
              
              {websiteUrl && (
                <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #e2e8f0' }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                    Learn more about {appName}:
                  </Typography>
                  <Button
                    variant="outlined"
                    href={websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      borderColor: appColor,
                      color: appColor,
                      '&:hover': {
                        borderColor: appColor,
                        backgroundColor: `${appColor}10`
                      }
                    }}
                  >
                    Visit {websiteUrl}
                  </Button>
                </Box>
              )}
            </Box>

            {/* For Uninstalled Users */}
            <Box sx={{ pt: 4, borderTop: '1px solid #e2e8f0' }}>
              <Typography variant="h5" gutterBottom sx={{ 
                color: appColor, 
                fontWeight: 600, 
                mb: 3,
                fontSize: { xs: '1.25rem', md: '1.5rem' }
              }}>
                For Uninstalled Users:
              </Typography>
              
              <Typography variant="h6" gutterBottom sx={{ 
                color: 'text.primary', 
                fontWeight: 600, 
                mb: 2 
              }}>
                Requesting Account Data Deletion via Email
              </Typography>
              
              <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.6 }}>
                If you've uninstalled {appName} and want to delete your account data, follow these steps:
              </Typography>

              <Box component="ul" sx={{ 
                color: 'text.secondary', 
                pl: 3, 
                mb: 4,
                '& li': { 
                  mb: 1.5, 
                  fontSize: { xs: '0.95rem', md: '1rem' },
                  lineHeight: 1.6 
                }
              }}>
                <li>Compose an email to <strong style={{ color: appColor }}>{supportEmail}</strong></li>
                <li>Use the subject line: "Account Data Deletion Request"</li>
                <li>In the body of the email, include the following details:
                  <Box component="ul" sx={{ 
                    mt: 1, 
                    pl: 2,
                    '& li': { 
                      mb: 1, 
                      fontSize: { xs: '0.9rem', md: '0.95rem' } 
                    }
                  }}>
                    <li>Your full name.</li>
                    <li>The email address or mobile number associated with your account.</li>
                    <li>Any additional information that may help us identify your account.</li>
                  </Box>
                </li>
                <li>Send the email.</li>
              </Box>

              <Typography variant="body1" sx={{ 
                color: 'text.secondary', 
                lineHeight: 1.6,
                fontStyle: 'italic' 
              }}>
                Our support team will process your request, and you'll receive a confirmation email once your account data has been deleted. Please note that this process may take a few days.
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default AccountSupport; 